import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface Notification {
  id: string;
  user_id: string;
  team_id: string | null;
  type: "report_viewed" | "seller_feedback" | "showing_scheduled" | "showing_feedback";
  title: string;
  body: string | null;
  reference_id: string | null;
  reference_type: string | null;
  reference_url: string | null;
  read_at: string | null;
  created_at: string;
}

const NOTIF_QUERY_KEY = (userId: string) => ["notifications", userId];

export function useNotificationsQuery(userId: string | undefined) {
  const queryClient = useQueryClient();

  // Realtime subscription
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`notifications-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: NOTIF_QUERY_KEY(userId) });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);

  return useQuery<Notification[]>({
    queryKey: NOTIF_QUERY_KEY(userId!),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", userId!)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return (data as Notification[]) ?? [];
    },
    enabled: !!userId,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useUnreadCount(userId: string | undefined): number {
  const { data } = useNotificationsQuery(userId);
  if (!data) return 0;
  return data.filter((n) => !n.read_at).length;
}

export function useMarkReadMutation(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("id", notificationId)
        .eq("user_id", userId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIF_QUERY_KEY(userId) });
    },
  });
}

export function useMarkAllReadMutation(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("user_id", userId)
        .is("read_at", null);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIF_QUERY_KEY(userId) });
    },
  });
}
